/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { FormikTextField } from "../../formik-fields/formik-text-field";
import { FormikRadioGroup } from "../../formik-fields/formik-radio-group";
import { PlanType } from "../../../models/enumerations/plan-type";
import { RadioGroupItem } from "../../radio-group/radio-group-item";
import { useFormikContext } from "formik";
import { PaymentCycle } from "../../../models/enumerations/payment-cycle";
import { PaymentCycleOptions, PaymentCycleEnterpriseOptions } from "../../../models/payment-cycle";
import { PaymentTypeOptions } from "../../../models/payment-type";
import { PlanTypeOptions } from '../../../models/plan-type';
import moment from "moment";
import { PaymentType } from "../../../models/enumerations/payment-type";
import { Grid } from "@mui/material";
import { FormikSelectField } from "../../formik-fields/formik-select-field";
import { NumericListOptions } from "../../../models/interfaces/numeric-list-options";
import { OrganizationType, OrganizationTypeLabel } from "../../../models/enumerations/organization-type";
import { LicenseLimit } from "../../../models/enumerations/license-limit";
import { UserLimit } from "../../../models/enumerations/user-limit";
import { useTranslation } from 'react-i18next';
import { TrialLengthItems } from "../trial-length-items";
import { FormikDateField } from "../../formik-fields/formik-date-field";
import { Button } from "../../button/button";
import WebhookSecretService from "../../../utilities/services/webhook-secret-service";
import { enqueueSnackbar } from "notistack";
import { createLogger } from "../../../utilities/logger";

const log = createLogger("PlanFields");

const accountTypeRadioItems: RadioGroupItem[] = [
    {
        value: OrganizationType.Paid,
        label: OrganizationTypeLabel[OrganizationType.Paid],
    },
    {
        value: OrganizationType.Trial,
        label: OrganizationTypeLabel[OrganizationType.Trial],
    },
];

interface UnboundTextFieldProps {
    setAccountType: Function,
    accountType: RadioGroupItem,
    loading: boolean,
    editing: boolean,
    startDateCallback: Function,
    initCustomTrialLength: boolean,
    organizationId?: string,
    externalApiKeyMasked?: string,
    externalApiKeyLoading?: boolean,
    onCopyKey?: () => void,
    onRotateKey?: () => void,
    rotateConfirmVisible?: boolean,
    onRotateConfirm?: () => void,
    onRotateCancel?: () => void,
}

/**
 * The form fields that are used for starter and business plans.
 */
export const PlanFields: React.FC<UnboundTextFieldProps> = (props) => {
    const {
        setAccountType,
        accountType,
        loading,
        editing,
        startDateCallback,
        initCustomTrialLength,
        organizationId,
        externalApiKeyMasked,
        externalApiKeyLoading,
        onCopyKey,
        onRotateKey,
        rotateConfirmVisible,
        onRotateConfirm,
        onRotateCancel,
    } = props;

    const formik = useFormikContext<any>();
    const { t } = useTranslation();
    const {
        setFieldValue,
        values,
        validateField,
    } = formik;

    const [customTrialLength, setCustomTrialLength] = useState<boolean>(initCustomTrialLength);
    const [planType, setPlanType] = useState<NumericListOptions>();
    const [paymentCycle, setPaymentCycle] = useState<PaymentCycle>();
    const [paymentType, setPaymentType] = useState<RadioGroupItem>(PaymentTypeOptions[0]);
    const [userLimitNonEnterprise, setUserLimitNonEnterprise] = useState<Number>(0);
    const [wellnessLimitNonEnterprise, setWellnessLimitNonEnterprise] = useState<Number>(0);
    const [priceNonEnterprise, setPriceNonEnterprise] = useState<Number>(0);
    const [lockLicense, setLockLicense] = useState<boolean>(true);
    const [webhookSecretStatus, setWebhookSecretStatus] = useState<{ exists: boolean; masked: string | null } | null>(null);
    const [secretLoading, setSecretLoading] = useState(false);
    const [secretBusy, setSecretBusy] = useState(false);
    const [secretRotateConfirm, setSecretRotateConfirm] = useState(false);
    useEffect(() => {
        if (paymentType !== values['paymentType']) {
            setPaymentType(values['paymentType'])
        }
    }, [values['paymentType']])
    useEffect(() => {
        setCustomTrialLength(initCustomTrialLength);
    }, [initCustomTrialLength]);
    useEffect(() => {
        const setFields = async () => {
            setPlanType(values['plan']);
            if (!values['paymentCycle'] && values['plan']?.value !== PlanType.External) {
                setFieldValue('paymentCycle', PaymentCycleOptions[0]);
                setPaymentCycle(PaymentCycleOptions[0].value);

            }
        }
        setFields();
    }, [values['plan']]);
    useEffect(() => {
        const setFields = async () => {
            if (planType !== null) {
                if (planType?.value === PlanType.Trial) {
                    await setFieldValue('userLimit', UserLimit.Trial);
                    validateField('userLimit');
                    setUserLimitNonEnterprise(UserLimit.Trial);
                    setWellnessLimitNonEnterprise(LicenseLimit.Trial);
                    await setFieldValue('mySymmioLicense', LicenseLimit.Trial);
                    setLockLicense(false);
                }
                if (planType?.value === PlanType.Core || planType?.value === PlanType.Pro) {
                    setLockLicense(false)
                }
                if (planType?.value === PlanType.Trial || planType?.value === PlanType.EnterpriseTrial) {
                    // reset renew date to value of trial length
                    if (!values.trialLength) {
                        setFieldValue('renewDate', undefined);
                    }
                    else {
                        handleTrialLengthChange(values.trialLength);
                        if (customTrialLength) {
                            handleCustomTrialLengthChange(values.customTrialLength)
                        }
                    }
                }
                if (planType?.value === PlanType.Enterprise || planType?.value === PlanType.EnterpriseTrial) {
                    setLockLicense(false);
                    if (paymentType?.value === PaymentType.FlatFee && !values?.price) {
                        setFieldValue('price', 1500);
                        setPriceNonEnterprise(1500);
                    }
                }

                if (planType?.value === PlanType.Business) {
                    setLockLicense(true);
                    await setFieldValue('mySymmioLicense', LicenseLimit.Business);
                    await setFieldValue('userLimit', UserLimit.Business);
                    setUserLimitNonEnterprise(UserLimit.Business);
                    setWellnessLimitNonEnterprise(LicenseLimit.Business);
                    validateField('userLimit');
                    if (paymentCycle === PaymentCycle.Monthly) {
                        setFieldValue('price', 75);
                        setPriceNonEnterprise(75);
                    }
                    if (paymentCycle === PaymentCycle.Yearly) {
                        setFieldValue('price', 765);
                        setPriceNonEnterprise(765);
                    }
                    if (paymentCycle === null) {
                        await setFieldValue('paymentCycle', PaymentCycleOptions[PaymentCycle.Monthly], false);
                        setPaymentCycle(PaymentCycleOptions[PaymentCycle.Monthly].value);
                        setFieldValue('price', 75);
                        setPriceNonEnterprise(75);
                    }
                }
                if (planType?.value === PlanType.Core) {
                    await setFieldValue('userLimit', UserLimit.Core);
                    validateField('userLimit');
                    setUserLimitNonEnterprise(UserLimit.Core);
                    setWellnessLimitNonEnterprise(LicenseLimit.Core);
                    await setFieldValue('mySymmioLicense', LicenseLimit.Core);
                }

                if (planType?.value === PlanType.Pro) {
                    await setFieldValue('userLimit', UserLimit.Pro);
                    validateField('userLimit');
                    setUserLimitNonEnterprise(UserLimit.Pro);
                    setWellnessLimitNonEnterprise(LicenseLimit.Pro);
                    await setFieldValue('mySymmioLicense', LicenseLimit.Pro);
                }

                if (planType?.value === PlanType.Enterprise) {
                    await setFieldValue('renewDate', values.renewDate ?? moment().add(1, 'month'));
                    validateField('renewDate');
                }

                if ((planType?.value === PlanType.Enterprise || planType?.value === PlanType.EnterpriseTrial) && !values.userLimit && !values.mySymmioLicense) {
                    await setFieldValue('userLimit', 250);
                    validateField('userLimit');
                    setUserLimitNonEnterprise(250);
                    await setFieldValue('mySymmioLicense', 250);
                }

                if (planType?.value === PlanType.ThirdParty) {
                    await setFieldValue('userLimit', UserLimit.ThirdParty);
                    setUserLimitNonEnterprise(UserLimit.ThirdParty);
                    validateField('userLimit');
                    setLockLicense(true);
                    await setFieldValue('mySymmioLicense', LicenseLimit.ThirdParty);
                }

                if (planType?.value === PlanType.External) {
                    await setFieldValue('userLimit', UserLimit.External);
                    setUserLimitNonEnterprise(UserLimit.External);
                    validateField('userLimit');
                    setLockLicense(true);
                    await setFieldValue('mySymmioLicense', LicenseLimit.External);
                }
            }
        }

        setFields();
    }, [planType]);

    useEffect(() => {
        const setFields = async () => {
            if (!loading && !!planType) {
                if (!values['startDate'] || !moment(values['startDate']).isValid()) {
                    setFieldValue('startDate', moment());
                }

                let renewDate = moment();
                // if creating a new organization, the user can manually input the
                // start date. In this case, we want to use the start date to
                // determine the renew date. If they haven't input a start date yet,
                // use the current date.
                if (!values['organizationId']) {
                    renewDate = values['startDate'] !== undefined ? moment(values['startDate']) : moment()
                }

                if (paymentCycle === null || paymentCycle === PaymentCycle.Monthly) {
                    renewDate.add(1, 'month');
                }

                if (paymentCycle === PaymentCycle.Quarterly) {
                    renewDate.add(3, 'month');
                }

                if (paymentCycle === PaymentCycle.Yearly) {
                    renewDate.add(1, 'year');
                }

                if (renewDate !== null) {
                    await setFieldValue('renewDate', renewDate);
                    validateField('renewDate');
                }
            }
        }
        setFields();
    }, [values['startDate']])


    useEffect(() => {
        if (planType?.value === PlanType.Enterprise) {
            if (paymentType.value === PaymentTypeOptions[0].value && !values.pricePerUser) {
                setFieldValue('pricePerUser', 3);
            }
            if (paymentType.value === PaymentTypeOptions[1].value && !values.price) {
                setFieldValue('price', 1500);
                setPriceNonEnterprise(1500);
            }
        }
    }, [paymentType]);

    useEffect(() => {
        if (editing && values['startDate']) {
            startDateCallback(values['startDate']);
        }
    }, [values['startDate']]);
    const handlePlanTypeChange = (option: NumericListOptions) => {
        // if we're switching plan types and the payment cycle is quarterly, we
        // need to change it to monthly because quarterly is only available for
        // enterprise plans
        if (values['paymentCycle'] === PaymentCycleEnterpriseOptions[1]) {
            setFieldValue('paymentCycle', PaymentCycleOptions[0])
            setPaymentCycle(PaymentCycle.Monthly);
        }
        if (option.value === PlanType.Trial || option.value === PlanType.EnterpriseTrial) {
            setFieldValue('accountType', accountTypeRadioItems[1])
            setAccountType(accountTypeRadioItems[1]);
        }
        else {
            setFieldValue('accountType', accountTypeRadioItems[0])
            setAccountType(accountTypeRadioItems[0]);
        }
        setPlanType(option);
    }

    const handlePaymentTypeChange = (type: RadioGroupItem) => {
        setPaymentType(type)
    }
    const handleTrialLengthChange = async (option: NumericListOptions) => {
        setCustomTrialLength(option.value === 0);
        if (option.value !== 0) {
            const exp = moment().add(option.value, 'days');
            await setFieldValue('renewDate', exp);
            validateField('renewDate');
        } else {
            setFieldValue('renewDate', null);
        }
    }
    const handleCustomTrialLengthChange = (days: any) => {
        if (days && days.match(/^([0-9])+$/)) {
            const intDays = parseInt(days, 10);
            const exp = moment().add(intDays, 'days');
            setFieldValue('renewDate', exp);
        }
    }



    const maskSecret = (secret: string) =>
        secret.length > 8 ? `${secret.slice(0, 4)}…${secret.slice(-4)}` : "••••";

    useEffect(() => {
        const loadSecretStatus = async () => {
            if (!editing || !organizationId || !values['webhookUrl']) {
                setWebhookSecretStatus(null);
                return;
            }
            setSecretLoading(true);
            try {
                const status = await WebhookSecretService.getStatus(organizationId);
                setWebhookSecretStatus(status);
            } catch (error) {
                setWebhookSecretStatus(null);
                log.error("Failed to load webhook signing secret status", {
                    exception: error,
                    organizationId,
                });
            } finally {
                setSecretLoading(false);
            }
        };
        loadSecretStatus();
    }, [editing, organizationId, values['webhookUrl']]);

    const copySecretToClipboard = async (secret: string) => {
        await navigator.clipboard.writeText(secret);
        enqueueSnackbar(t('organization.edit.fields.webhookSecretCopied'), { variant: "toast", width: "450px", autoHideDuration: 5000 });
    };

    const handleGenerateSecret = async () => {
        if (!organizationId) return;
        setSecretBusy(true);
        try {
            const { secret } = await WebhookSecretService.generate(organizationId);
            setWebhookSecretStatus({ exists: true, masked: maskSecret(secret) });
            enqueueSnackbar(t('organization.edit.fields.webhookSecretGenerated'), { variant: "toast", width: "450px", autoHideDuration: 5000 });
        } catch (error) {
            log.error("Failed to generate webhook signing secret", {
                exception: error,
                organizationId,
            });
        } finally {
            setSecretBusy(false);
        }
    };

    const handleCopySecret = async () => {
        if (!organizationId) return;
        setSecretBusy(true);
        try {
            const { secret } = await WebhookSecretService.reveal(organizationId);
            await copySecretToClipboard(secret);
        } catch (error) {
            log.error("Failed to reveal webhook signing secret", {
                exception: error,
                organizationId,
            });
        } finally {
            setSecretBusy(false);
        }
    };

    const handleRotateSecret = async () => {
        if (!organizationId) return;
        setSecretRotateConfirm(false);
        setSecretBusy(true);
        try {
            const { secret } = await WebhookSecretService.rotate(organizationId);
            setWebhookSecretStatus({ exists: true, masked: maskSecret(secret) });
            enqueueSnackbar(t('organization.edit.fields.webhookSecretRotated'), { variant: "toast", width: "450px", autoHideDuration: 5000 });
        } catch (error) {
            log.error("Failed to rotate webhook signing secret", {
                exception: error,
                organizationId,
            });
        } finally {
            setSecretBusy(false);
        }
    };

    return (
        <div>
            <>
                {
                    <>
                        <Grid container rowSpacing={4} columnSpacing={3}>
                            <Grid item sm={12} md={(planType?.value !== PlanType.ThirdParty && planType?.value !== PlanType.External) ? 6 : 12}>
                                <FormikSelectField
                                    label={t('organization.edit.fields.plan')}
                                    name="plan"
                                    items={PlanTypeOptions}
                                    includeNone
                                    onChange={handlePlanTypeChange}
                                    readOnly={editing && (planType?.value === PlanType.Core || planType?.value === PlanType.Pro || planType?.value === PlanType.Business)}
                                    hidden={editing && (planType?.value === PlanType.Core || planType?.value === PlanType.Pro || planType?.value === PlanType.Business)}
                                    disabled={loading}
                                    required
                                />
                                {
                                    editing && (planType?.value === PlanType.Core || planType?.value === PlanType.Pro || planType?.value === PlanType.Business) && (
                                        <div className="non-enterprise-field">
                                            <label>{t('organization.edit.fields.plan')}</label>
                                            {planType.label}
                                        </div>
                                    )

                                }
                            </Grid>
                            {
                                planType?.value === PlanType.Enterprise &&
                                <Grid item sm={12} md={6}>
                                    <FormikSelectField
                                        label={t('organization.edit.fields.paymentCycle')}
                                        name="paymentCycle"
                                        items={PaymentCycleEnterpriseOptions}
                                        includeNone
                                        disabled={loading}
                                        onChange={(o: NumericListOptions) => setPaymentCycle(o.value)}
                                        required
                                    />
                                </Grid>
                            }
                            {
                                accountType?.value === OrganizationType.Trial && (
                                    <Grid item sm={12} md={6}>
                                        <FormikSelectField
                                            name='trialLength'
                                            label={t('organization.edit.fields.trialLength')}
                                            disabled={loading}
                                            items={TrialLengthItems}
                                            onChange={handleTrialLengthChange}
                                            includeNone
                                            required
                                        />
                                    </Grid>
                                )
                            }
                            {
                                (accountType?.value === OrganizationType.Trial && customTrialLength) &&
                                <Grid item sm={12} md={6}>
                                    <FormikTextField
                                        name='customTrialLength'
                                        label={t('organization.edit.fields.customTrialLength')}
                                        onChange={handleCustomTrialLengthChange}
                                        disabled={loading}
                                        required
                                    />
                                </Grid>
                            }
                            {
                                planType && planType.value !== PlanType.ThirdParty && planType.value !== PlanType.External &&
                                <Grid item sm={12} md={(customTrialLength && planType.value !== PlanType.Enterprise) ? 6 : 12}>
                                    <FormikDateField
                                        name="renewDate"
                                        label={t('organization.edit.fields.renewDate')}
                                        minDate={values['startDate']}
                                        disabled={loading}
                                        readOnly={accountType.value === OrganizationType.Trial}
                                        required
                                    />
                                </Grid>
                            }
                            {
                                (planType?.value === PlanType.Enterprise) &&
                                <Grid item xs={12}>
                                    <FormikRadioGroup
                                        label={t('organization.edit.fields.paymentModel')}
                                        name="paymentType"
                                        items={PaymentTypeOptions}
                                        value={values['paymentType'] ?? paymentType ?? PaymentTypeOptions[0]}
                                        onChange={handlePaymentTypeChange}
                                    />
                                </Grid>
                            }
                            {
                                (planType?.value !== PlanType.ThirdParty && planType?.value !== PlanType.External) && (
                                    <Grid item sm={12} md={6}>
                                        <FormikTextField
                                            name="userLimit"
                                            label={t('organization.edit.fields.userLimit')}
                                            disabled={loading}
                                            readOnly={planType?.value !== PlanType.Enterprise && planType?.value !== PlanType.EnterpriseTrial}
                                            hidden={planType?.value !== PlanType.Enterprise && planType?.value !== PlanType.EnterpriseTrial}
                                            required
                                        />

                                        {
                                            (planType?.value && planType?.value !== PlanType.Enterprise && planType?.value !== PlanType.EnterpriseTrial) &&
                                            <div className="non-enterprise-field">
                                                <label>{t('organization.edit.fields.userLimit')}</label>
                                                {userLimitNonEnterprise.toString()}
                                            </div>
                                        }
                                    </Grid>
                                )
                            }
                            {
                                (planType?.value === PlanType.Trial || planType?.value === PlanType.Core || planType?.value === PlanType.Pro || planType?.value === PlanType.Business) && (
                                    <Grid item sm={12} md={6}>
                                        <FormikTextField
                                            name="wellnessLimit"
                                            label={t('organization.edit.fields.wellnessLimit')}
                                            disabled={loading}
                                            readOnly={true}
                                            hidden={true}
                                            required
                                        />
                                        <div className="non-enterprise-field">
                                            <label>{t('organization.edit.fields.wellnessLimit')}</label>
                                            {wellnessLimitNonEnterprise.toString()}
                                        </div>
                                    </Grid>
                                )
                            }
                            {(planType?.value === PlanType.Enterprise) && (
                                <Grid item sm={12} md={6}>
                                    {
                                        (planType?.value === PlanType.Enterprise && paymentType?.value === PaymentType.PayPerUser)
                                            ? (
                                                <FormikTextField
                                                    name="pricePerUser"
                                                    label={t('organization.edit.fields.pricePerUser')}
                                                    disabled={loading}
                                                    startAdornment="$"
                                                    required
                                                />
                                            ) :
                                            (
                                                <>
                                                    <FormikTextField
                                                        name="price"
                                                        label={t('organization.edit.fields.price')}
                                                        startAdornment="$"
                                                        disabled={loading}
                                                        readOnly={planType?.value !== PlanType.Enterprise}
                                                        hidden={planType?.value !== PlanType.Enterprise}
                                                        required
                                                    />
                                                    {
                                                        (planType?.value !== PlanType.Enterprise) &&
                                                        <div className="non-enterprise-field">
                                                            <label>{t('organization.edit.fields.price')}</label>
                                                            {"$" + priceNonEnterprise.toString()}
                                                        </div>
                                                    }
                                                </>
                                            )
                                    }
                                </Grid>
                            )}
                            {planType?.value === PlanType.ThirdParty && (
                                <Grid item sm={12} md={12}>
                                    <FormikTextField
                                        name="subscriptionName"
                                        label={t('organization.edit.fields.subscriptionName')}
                                        required
                                        disabled={loading}
                                    />
                                </Grid>
                            )

                            }
                            {planType?.value === PlanType.ThirdParty && (
                                <Grid item sm={12} md={6}>
                                    <FormikTextField
                                        name="mySymmioYearlyPrice"
                                        label={t('organization.edit.fields.yearlyPrice')}
                                        required
                                        disabled={loading}
                                    />
                                </Grid>
                            )}
                            {planType?.value === PlanType.ThirdParty && (
                                <Grid item sm={12} md={6}>
                                    <FormikTextField
                                        name="mySymmioMonthlyPrice"
                                        label={t('organization.edit.fields.monthlyPrice')}
                                        required
                                        disabled={loading}
                                    />
                                </Grid>
                            )}
                            {planType?.value === PlanType.External && (
                                <>
                                    <Grid item sm={12} md={6}>
                                        <FormikSelectField
                                            label={t('organization.edit.fields.billingPeriod')}
                                            name="paymentCycle"
                                            items={PaymentCycleOptions}
                                            includeNone
                                            disabled={loading}
                                        />
                                    </Grid>
                                    <Grid item sm={12} md={6}>
                                        <FormikDateField
                                            name="billingStartDate"
                                            label={t('organization.edit.fields.billingStartDate')}
                                            disabled={loading}
                                        />
                                    </Grid>
                                    {editing && externalApiKeyMasked && (
                                        <Grid item xs={12}>
                                            <p style={{ fontSize: '12px', fontWeight: '700', marginBottom: '8px' }}>
                                                {t('organization.edit.fields.apiKey')}
                                            </p>
                                            <p style={{ fontSize: '14px', fontWeight: '300', fontFamily: 'monospace', marginBottom: '12px' }}>
                                                {externalApiKeyLoading ? '...' : externalApiKeyMasked}
                                            </p>
                                            <div style={{ display: 'flex', gap: '12px' }}>
                                                {onCopyKey && (
                                                    <Button
                                                        buttonStyle='primary'
                                                        buttonText={t('organization.edit.fields.copyKey')}
                                                        onClick={onCopyKey}
                                                        disabled={externalApiKeyLoading}
                                                        buttonType='button'
                                                    />
                                                )}
                                                {onRotateKey && !rotateConfirmVisible && (
                                                    <Button
                                                        buttonStyle='secondary'
                                                        buttonText={t('organization.edit.fields.rotateKey')}
                                                        onClick={onRotateKey}
                                                        disabled={externalApiKeyLoading}
                                                        buttonType='button'
                                                    />
                                                )}
                                                {rotateConfirmVisible && onRotateConfirm && onRotateCancel && (
                                                    <>
                                                        <Button
                                                            buttonStyle='primary'
                                                            buttonText={t('buttons.btn_confirm')}
                                                            onClick={onRotateConfirm}
                                                            disabled={externalApiKeyLoading}
                                                            buttonType='button'
                                                        />
                                                        <Button
                                                            buttonStyle='secondary'
                                                            buttonText={t('buttons.btn_cancel')}
                                                            onClick={onRotateCancel}
                                                            disabled={externalApiKeyLoading}
                                                            buttonType='button'
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        </Grid>
                                    )}
                                </>
                            )}
                            {
                                (planType?.value === PlanType.Enterprise || planType?.value === PlanType.EnterpriseTrial) &&
                                <>
                                    <Grid item sm={12} md={6}>
                                        <FormikTextField
                                            name="availableOrganizations"
                                            label={t('organization.edit.fields.organizationsAvailable')}
                                            required
                                            disabled={loading}
                                        />
                                    </Grid>
                                </>
                            }
                            {
                                planType?.value && planType?.value !== PlanType.ThirdParty && planType?.value !== PlanType.External && planType?.value !== PlanType.Trial && planType?.value !== PlanType.Core && planType?.value !== PlanType.Pro && planType?.value !== PlanType.Business && (
                                    <Grid item sm={12} md={6}>
                                        <FormikTextField
                                            name="mySymmioLicense"
                                            label={t('organization.edit.fields.programs')}
                                            required
                                            disabled={loading || lockLicense}
                                        />
                                    </Grid>
                                )
                            }
                            {
                                (planType?.value === PlanType.Trial || planType?.value === PlanType.Core || planType?.value === PlanType.Pro) && (
                                    <>
                                        <Grid item sm={12} md={6}>
                                            <FormikTextField
                                                name="additionalUserLimit"
                                                label={t('organization.edit.fields.additionalUserLimit')}
                                                required
                                                disabled={loading || lockLicense}
                                            />
                                        </Grid>
                                        <Grid item sm={12} md={6}>
                                            <FormikTextField
                                                name="additionalWellnessLimit"
                                                label={t('organization.edit.fields.additionalWellnessLimit')}
                                                required
                                                disabled={loading || lockLicense}
                                            />
                                        </Grid>
                                    </>
                                )
                            }
                            {planType?.value !== PlanType.External && editing && organizationId && values['webhookUrl'] && (
                                <Grid item xs={12}>
                                    <p style={{ fontSize: '12px', fontWeight: '700', marginBottom: '8px' }}>
                                        {t('organization.edit.fields.webhookSecret')}
                                    </p>
                                    {webhookSecretStatus?.exists ? (
                                        <>
                                            <p style={{ fontSize: '14px', fontWeight: '300', fontFamily: 'monospace', marginBottom: '12px' }}>
                                                {webhookSecretStatus.masked}
                                            </p>
                                            <div style={{ display: 'flex', gap: '12px' }}>
                                                <Button
                                                    buttonStyle='primary'
                                                    buttonText={t('organization.edit.fields.webhookSecretCopy')}
                                                    onClick={handleCopySecret}
                                                    disabled={secretBusy}
                                                    buttonType='button'
                                                />
                                                {!secretRotateConfirm && (
                                                    <Button
                                                        buttonStyle='secondary'
                                                        buttonText={t('organization.edit.fields.webhookSecretRotate')}
                                                        onClick={() => setSecretRotateConfirm(true)}
                                                        disabled={secretBusy}
                                                        buttonType='button'
                                                    />
                                                )}
                                                {secretRotateConfirm && (
                                                    <>
                                                        <Button
                                                            buttonStyle='primary'
                                                            buttonText={t('buttons.btn_confirm')}
                                                            onClick={handleRotateSecret}
                                                            disabled={secretBusy}
                                                            buttonType='button'
                                                        />
                                                        <Button
                                                            buttonStyle='secondary'
                                                            buttonText={t('buttons.btn_cancel')}
                                                            onClick={() => setSecretRotateConfirm(false)}
                                                            disabled={secretBusy}
                                                            buttonType='button'
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <p style={{ fontSize: '12px', fontWeight: '300', marginBottom: '12px', color: 'rgba(18, 22, 34, 0.6)' }}>
                                                {t('organization.edit.fields.webhookSecretHint')}
                                            </p>
                                            <Button
                                                buttonStyle='primary'
                                                buttonText={t('organization.edit.fields.webhookSecretGenerate')}
                                                onClick={handleGenerateSecret}
                                                disabled={secretBusy || secretLoading}
                                                buttonType='button'
                                            />
                                        </>
                                    )}
                                </Grid>
                            )}
                        </Grid>
                    </>

                }
            </>
        </div>
    );
};
